import type { FolioRichContentPayload } from '@contract-analyzer/api-client';
import { z } from 'zod';

import {
  formatFolioModeLabel,
  jsonObjectSchema,
  jsonValueSchema,
  parseChartData,
  parseFolioMode,
  parseScalarData,
  parseTableData,
  type JsonObject,
  type JsonValue,
} from '@/hooks/folio-payload-schemas';
import type { CellEvidence } from '@/lib/cell-evidence';
import type {
  ActionCard,
  CellExplanation,
  CitationDocument,
  CitationGroup,
  CodeExecution,
  CodePlot,
  EvidenceLevelCount,
  FieldStats,
  FolioStreamEvent,
  GuideStep,
  GuideStepOverview,
  NavigationCta,
  ReasoningSummary,
  ToolCallTrace,
  UserQuestion,
  UserQuestionField,
} from '@/hooks/folio-types';

const nullableStringSchema = z.string().nullable().optional();
const nullableNumberSchema = z.number().nullable().optional();
const stringArraySchema = z.array(z.string());
const numberRecordSchema = z.record(z.string(), z.number());
const eventTypeSchema = z.enum([
  'text_delta',
  'error',
  'reasoning_summary',
  'tool_call',
  'chart',
  'citation_group',
  'table',
  'scalar',
  'action_card',
  'guide_step',
  'mode_change',
  'code_execution',
  'code_partial_output',
  'cell_explanation',
  'field_stats',
  'user_question',
  'navigation_cta',
  'done',
]);

type EventType = z.infer<typeof eventTypeSchema>;
type EventDecoder = (dataText: string) => FolioStreamEvent | null;

const citationDocumentSchema = z.object({
  document_id: z.string(),
  document_name: z.string(),
  excerpt: z.string(),
  page_number: nullableNumberSchema,
}).transform(({ page_number, ...citation }): CitationDocument => ({
  ...citation,
  ...(page_number == null ? {} : { page_number }),
}));
const currentCitationGroupSchema = z.object({
  claim: z.string(),
  citations: z.array(jsonValueSchema),
});
const reasoningSummarySchema = z.object({
  summary: z.string(),
  extra: jsonObjectSchema.nullable().optional(),
});
const toolCallSchema = z.object({
  tool_name: z.string(),
  status: z.enum(['completed', 'failed', 'cancelled']),
  summary: nullableStringSchema,
  arguments: jsonObjectSchema.nullable().optional(),
  result_keys: stringArraySchema.nullable().optional(),
});
const actionCardSchema = z.object({
  type: z.string(),
  title: z.string(),
  description: nullableStringSchema,
  diff: jsonObjectSchema.nullable().optional(),
  payload: jsonObjectSchema.nullable().optional(),
});
const guideStepSchema = z.object({
  step: z.number(),
  total: z.number(),
  title: z.string(),
  description: z.string(),
  steps_overview: jsonValueSchema.optional(),
});
const guideStepOverviewSchema = z.array(z.object({ step: z.number(), title: z.string() })).min(1);
const codePlotSchema = z.object({ format: z.literal('png'), content_base64: z.string() });
const codeExecutionSchema = z.object({
  status: z.enum(['started', 'completed', 'failed']),
  result: jsonObjectSchema.nullable().optional(),
  error: nullableStringSchema,
  code: nullableStringSchema,
  source_code: nullableStringSchema,
  intent: nullableStringSchema,
  output_type: nullableStringSchema,
  plots: z.array(jsonValueSchema).nullable().optional(),
  stdout: nullableStringSchema,
  stderr: nullableStringSchema,
  cell_count: nullableNumberSchema,
  was_reset: z.boolean().nullable().optional(),
  recycled_reason: nullableStringSchema,
});
const codePartialOutputSchema = z.object({
  channel: z.enum(['stdout', 'stderr']),
  chunk: z.string(),
});
const evidenceLevelSchema = z.enum([
  'reviewer_confirmed',
  'grounded',
  'needs_check',
  'unsupported',
  'not_found',
  'not_assessed',
]);
const cellEvidenceSchema = z
  .object({
    level: evidenceLevelSchema,
    machine_level: evidenceLevelSchema.nullable().optional(),
    reasons: z.array(z.string()).min(1),
  })
  .transform(
    ({ level, machine_level, reasons }): CellEvidence => ({
      level,
      machine_level: machine_level ?? null,
      reasons,
    }),
  );
const evidenceDistributionSchema = z.array(
  z.object({ level: evidenceLevelSchema, count: z.number().int().nonnegative() }),
);
const cellExplanationSchema = z.object({
  document_name: z.string(),
  field_name: z.string(),
  value: nullableStringSchema,
  status: nullableStringSchema,
  rationale: nullableStringSchema,
  display_value: nullableStringSchema,
  field_type: nullableStringSchema,
  extraction_guidance: nullableStringSchema,
  citations: z.array(citationDocumentSchema).nullable().optional(),
  evidence: jsonValueSchema.optional(),
});
const fieldStatsSchema = z.object({
  field_name: z.string(),
  total_documents: z.number(),
  extracted_count: z.number(),
  not_found_count: z.number(),
  sample_values: stringArraySchema.nullable().optional(),
  field_type: nullableStringSchema,
  extraction_guidance: nullableStringSchema,
  status_counts: jsonObjectSchema.nullable().optional(),
  evidence_distribution: jsonValueSchema.optional(),
});
const userQuestionFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'textarea', 'select', 'number', 'boolean']),
  placeholder: nullableStringSchema,
  options: stringArraySchema.nullable().optional(),
  required: z.boolean().nullable().optional(),
  default_value: nullableStringSchema,
});
const userQuestionSchema = z.object({
  question: z.string(),
  fields: z.array(jsonValueSchema),
});
const navigationCtaSchema = z.object({
  task_id: z.string(),
  page_id: z.string(),
  page_name: z.string(),
  deep_link: z.string().startsWith('/').refine((path) => !path.startsWith('//')),
});
const modeChangeSchema = z.object({
  mode: jsonValueSchema,
  label: nullableStringSchema,
  reason: nullableStringSchema,
});
const doneSchema = z.object({ assistant_message_id: z.uuid().nullable() });

function parseJson(dataText: string): JsonValue | null {
  try {
    const result = jsonValueSchema.safeParse(JSON.parse(dataText));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function optionalString(value: string | null | undefined): string | undefined {
  return value ?? undefined;
}

function optionalNumber(value: number | null | undefined): number | undefined {
  return value ?? undefined;
}

function optionalObject(value: JsonObject | null | undefined): JsonObject | undefined {
  return value ?? undefined;
}

function parseCitation(value: JsonValue): CitationDocument | null {
  const result = citationDocumentSchema.safeParse(value);
  return result.success ? result.data : null;
}

function parseTextDelta(dataText: string): FolioStreamEvent | null {
  const parsed = z.object({ content: z.string() }).safeParse(parseJson(dataText));
  return parsed.success ? { type: 'text_delta', content: parsed.data.content } : null;
}

function parseError(dataText: string): FolioStreamEvent | null {
  const parsed = z.object({ error: z.string().min(1) }).safeParse(parseJson(dataText));
  return parsed.success ? { type: 'error', message: parsed.data.error } : null;
}

function parseReasoningSummary(dataText: string): FolioStreamEvent | null {
  const result = reasoningSummarySchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const extra = optionalObject(result.data.extra);
  const data: ReasoningSummary = {
    summary: result.data.summary,
    ...(extra === undefined ? {} : { extra }),
  };
  return { type: 'reasoning_summary', data };
}

function parseToolCall(dataText: string): FolioStreamEvent | null {
  const result = toolCallSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const summary = optionalString(result.data.summary);
  const argumentsValue = optionalObject(result.data.arguments);
  const resultKeys = result.data.result_keys ?? undefined;
  const data: ToolCallTrace = {
    tool_name: result.data.tool_name,
    status: result.data.status,
    ...(summary === undefined ? {} : { summary }),
    ...(argumentsValue === undefined ? {} : { arguments: argumentsValue }),
    ...(resultKeys === undefined ? {} : { result_keys: resultKeys }),
  };
  return { type: 'tool_call', data };
}

function parseChart(dataText: string): FolioStreamEvent | null {
  const data = parseChartData(parseJson(dataText));
  return data === null ? null : { type: 'chart', data };
}

function parseCitationGroup(dataText: string): FolioStreamEvent | null {
  const value = parseJson(dataText);
  const current = currentCitationGroupSchema.safeParse(value);
  if (current.success) {
    const citations = current.data.citations
      .map(parseCitation)
      .filter((citation): citation is CitationDocument => citation !== null);
    const data: CitationGroup = { claim: current.data.claim, citations };
    return citations.length > 0 ? { type: 'citation_group', data } : null;
  }

  return null;
}

function parseTable(dataText: string): FolioStreamEvent | null {
  const data = parseTableData(parseJson(dataText));
  return data === null ? null : { type: 'table', data };
}

function parseScalar(dataText: string): FolioStreamEvent | null {
  const data = parseScalarData(parseJson(dataText));
  return data === null ? null : { type: 'scalar', data };
}

function parseActionCard(dataText: string): FolioStreamEvent | null {
  const result = actionCardSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const description = optionalString(result.data.description);
  const diff = optionalObject(result.data.diff);
  const payload = optionalObject(result.data.payload);
  const data: ActionCard = {
    type: result.data.type,
    title: result.data.title,
    ...(description === undefined ? {} : { description }),
    ...(diff === undefined ? {} : { diff }),
    ...(payload === undefined ? {} : { payload }),
  };
  return { type: 'action_card', data };
}

function parseGuideStep(dataText: string): FolioStreamEvent | null {
  const result = guideStepSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const overviewResult = guideStepOverviewSchema.safeParse(result.data.steps_overview);
  const stepsOverview: readonly GuideStepOverview[] | undefined = overviewResult.success
    ? overviewResult.data
    : undefined;
  const data: GuideStep = {
    step: result.data.step,
    total: result.data.total,
    title: result.data.title,
    description: result.data.description,
    ...(stepsOverview === undefined ? {} : { steps_overview: stepsOverview }),
  };
  return { type: 'guide_step', data };
}

function parsePlots(values: JsonValue[] | null | undefined): readonly CodePlot[] | undefined {
  if (values === null || values === undefined) {
    return undefined;
  }
  const plots = values.flatMap((value) => {
    const result = codePlotSchema.safeParse(value);
    return result.success ? [result.data] : [];
  });
  return plots.length > 0 ? plots : undefined;
}

function parseCodeExecution(dataText: string): FolioStreamEvent | null {
  const result = codeExecutionSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const value = result.data;
  const parsedResult = optionalObject(value.result);
  const error = optionalString(value.error);
  const code = optionalString(value.code);
  const sourceCode = optionalString(value.source_code);
  const intent = optionalString(value.intent);
  const outputType = optionalString(value.output_type);
  const plots = parsePlots(value.plots);
  const stdout = optionalString(value.stdout);
  const stderr = optionalString(value.stderr);
  const cellCount = optionalNumber(value.cell_count);
  const wasReset = value.was_reset ?? undefined;
  const recycledReason = optionalString(value.recycled_reason);
  const data: CodeExecution = {
    status: value.status,
    ...(parsedResult === undefined ? {} : { result: parsedResult }),
    ...(error === undefined ? {} : { error }),
    ...(code === undefined ? {} : { code }),
    ...(sourceCode === undefined ? {} : { source_code: sourceCode }),
    ...(intent === undefined ? {} : { intent }),
    ...(outputType === undefined ? {} : { output_type: outputType }),
    ...(plots === undefined ? {} : { plots }),
    ...(stdout === undefined ? {} : { stdout }),
    ...(stderr === undefined ? {} : { stderr }),
    ...(cellCount === undefined ? {} : { cell_count: cellCount }),
    ...(wasReset === undefined ? {} : { was_reset: wasReset }),
    ...(recycledReason === undefined ? {} : { recycled_reason: recycledReason }),
  };
  return { type: 'code_execution', data };
}

function parseCodePartialOutput(dataText: string): FolioStreamEvent | null {
  const result = codePartialOutputSchema.safeParse(parseJson(dataText));
  return result.success ? { type: 'code_partial_output', data: result.data } : null;
}

/**
 * Evidence is read apart from the rest of a card, so a level this build does not
 * know drops only the evidence, never the card. `null` (no answer to assess, or a
 * card saved before evidence levels) reads as absent.
 */
function parseCellEvidence(value: JsonValue | undefined): CellEvidence | undefined {
  const result = cellEvidenceSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

function parseEvidenceDistribution(
  value: JsonValue | undefined,
): readonly EvidenceLevelCount[] | undefined {
  const result = evidenceDistributionSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

function parseCellExplanation(dataText: string): FolioStreamEvent | null {
  const result = cellExplanationSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const value = result.data;
  const cellValue = value.value ?? value.display_value ?? value.status;
  if (cellValue === null || cellValue === undefined) {
    return null;
  }
  const status = optionalString(value.status);
  const rationale = optionalString(value.rationale);
  const displayValue = optionalString(value.display_value);
  const fieldType = optionalString(value.field_type);
  const extractionGuidance = optionalString(value.extraction_guidance);
  const evidence = parseCellEvidence(value.evidence);
  const data: CellExplanation = {
    document_name: value.document_name,
    field_name: value.field_name,
    value: cellValue,
    ...(status === undefined ? {} : { status }),
    ...(rationale === undefined ? {} : { rationale }),
    ...(displayValue === undefined ? {} : { display_value: displayValue }),
    ...(fieldType === undefined ? {} : { field_type: fieldType }),
    ...(extractionGuidance === undefined ? {} : { extraction_guidance: extractionGuidance }),
    citations: value.citations ?? [],
    ...(evidence === undefined ? {} : { evidence }),
  };
  return { type: 'cell_explanation', data };
}

function parseNumberRecord(value: JsonObject | null | undefined): Readonly<Record<string, number>> {
  const result = numberRecordSchema.safeParse(value);
  return result.success ? result.data : {};
}

function parseFieldStats(dataText: string): FolioStreamEvent | null {
  const result = fieldStatsSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const value = result.data;
  const fieldType = optionalString(value.field_type);
  const extractionGuidance = optionalString(value.extraction_guidance);
  const statusCounts = parseNumberRecord(value.status_counts);
  const evidenceDistribution = parseEvidenceDistribution(value.evidence_distribution);
  const data: FieldStats = {
    field_name: value.field_name,
    total_documents: value.total_documents,
    extracted_count: value.extracted_count,
    not_found_count: value.not_found_count,
    sample_values: value.sample_values ?? [],
    ...(fieldType === undefined ? {} : { field_type: fieldType }),
    ...(extractionGuidance === undefined ? {} : { extraction_guidance: extractionGuidance }),
    ...(Object.keys(statusCounts).length === 0 ? {} : { status_counts: statusCounts }),
    ...(evidenceDistribution === undefined ? {} : { evidence_distribution: evidenceDistribution }),
  };
  return { type: 'field_stats', data };
}

function parseQuestionField(value: JsonValue): UserQuestionField | null {
  const result = userQuestionFieldSchema.safeParse(value);
  if (!result.success) {
    return null;
  }
  const field = result.data;
  const placeholder = optionalString(field.placeholder);
  const options = field.options ?? undefined;
  const required = field.required ?? undefined;
  const defaultValue = optionalString(field.default_value);
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    ...(placeholder === undefined ? {} : { placeholder }),
    ...(options === undefined ? {} : { options }),
    ...(required === undefined ? {} : { required }),
    ...(defaultValue === undefined ? {} : { default_value: defaultValue }),
  };
}

function parseUserQuestion(dataText: string): FolioStreamEvent | null {
  const result = userQuestionSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const fields = result.data.fields
    .map(parseQuestionField)
    .filter((field): field is UserQuestionField => field !== null);
  const data: UserQuestion = { question: result.data.question, fields };
  return fields.length > 0 ? { type: 'user_question', data } : null;
}

function parseNavigationCta(dataText: string): FolioStreamEvent | null {
  const result = navigationCtaSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    console.warn('Ignoring Folio navigation_cta event with an invalid payload.');
    return null;
  }
  const data: NavigationCta = result.data;
  return { type: 'navigation_cta', data };
}

function parseModeChange(dataText: string): FolioStreamEvent | null {
  const result = modeChangeSchema.safeParse(parseJson(dataText));
  if (!result.success) {
    return null;
  }
  const mode = parseFolioMode(result.data.mode);
  if (mode === null) {
    console.warn('Ignoring Folio mode_change event with invalid mode.', result.data.mode);
    return null;
  }
  const label = optionalString(result.data.label) ?? formatFolioModeLabel(mode);
  const reason = optionalString(result.data.reason);
  return {
    type: 'mode_change',
    mode,
    label,
    ...(reason === undefined ? {} : { reason }),
  };
}

function parseDone(dataText: string): FolioStreamEvent | null {
  const parsed = doneSchema.safeParse(parseJson(dataText));
  if (!parsed.success) return null;
  return parsed.data.assistant_message_id === null
    ? { type: 'done' }
    : { type: 'done', assistantMessageId: parsed.data.assistant_message_id };
}

const eventDecoders = {
  text_delta: parseTextDelta,
  error: parseError,
  reasoning_summary: parseReasoningSummary,
  tool_call: parseToolCall,
  chart: parseChart,
  citation_group: parseCitationGroup,
  table: parseTable,
  scalar: parseScalar,
  action_card: parseActionCard,
  guide_step: parseGuideStep,
  mode_change: parseModeChange,
  code_execution: parseCodeExecution,
  code_partial_output: parseCodePartialOutput,
  cell_explanation: parseCellExplanation,
  field_stats: parseFieldStats,
  user_question: parseUserQuestion,
  navigation_cta: parseNavigationCta,
  done: parseDone,
} satisfies Record<EventType, EventDecoder>;

export function decodeFolioEvent(
  eventType: string | null,
  dataText: string,
): FolioStreamEvent | null {
  const result = eventTypeSchema.safeParse(eventType);
  return result.success ? eventDecoders[result.data](dataText) : null;
}

export function parseStoredRichEvent(
  payload: FolioRichContentPayload,
): FolioStreamEvent | null {
  // Stored payloads were normalized by FolioMessageResponse. They still cross
  // a network boundary, so run them through the same runtime schemas as SSE.
  return decodeFolioEvent(payload.event, JSON.stringify(payload));
}
